/**
 * Persona onboarding UX hardening (2026-09-12). Relocated (not duplicated)
 * from the old combined `PersonasTab.tsx` so both the Create screen's
 * copy and the separate Reference Upload screen's disable-at-cap logic
 * share exactly one source of truth for "how many reference photos."
 *
 * This is the FIXED Phota-training target — the same number the backend
 * enforces as its self-serve upload cap (`min(plan referenceAssetLimit,
 * PHOTA_TRAINING_REQUIRED_IMAGE_COUNT)`, see `add-persona-reference-image.ts`).
 * Deliberately NOT imported from `@emma/application` — the dashboard has
 * no dependency on the backend application package, by design (a
 * genuinely different package boundary, not an oversight) — so this
 * value is mirrored here and asserted to match the backend's real
 * behavior via API-level tests, never a value pulled across that
 * boundary.
 *
 * Product decision (2026-09-12, five-image onboarding): lowered from 50
 * to 5 — a real controlled test the same day showed a small, deliberately
 * curated five-image set (one photo per recommended view below) matched
 * or exceeded a 50-image set's identity fidelity. There is no SFW/NSFW
 * split for these five images.
 */
export const CANONICAL_COMPLETE_REFERENCE_SET = 5;

/**
 * FIX-006 (Real Progress UX, 2026-09-13): mirrors the backend's own
 * canonical `PERSONA_PHOTA_PREPARATION_PERIOD_MS`
 * (`derive-persona-phota-readiness-state.ts`) — the fixed duration of
 * the mandatory Preparing window after a Phota Profile first reaches
 * READY. Same package-boundary reasoning as `CANONICAL_COMPLETE_REFERENCE_SET`
 * above: the dashboard has no dependency on `@emma/application`, so this
 * value is mirrored here rather than imported. Used ONLY to render the
 * Preparing countdown/progress bar — the server (`photaReadinessState`)
 * remains the sole authority on whether the Persona is actually Ready.
 */
export const PERSONA_PHOTA_PREPARATION_PERIOD_MS = 15 * 60 * 1000;

/** The exact required first-count copy — the User chooses which images to upload; VIDEO STUDIO never classifies, tags, or buckets them. */
export const REQUIRED_TRAINING_IMAGE_COUNT_LABEL = "5";

/**
 * The five recommended views, shown identically on the Create/Upload
 * screen and in the Manual — one shared source of truth so the two
 * never drift apart. VIDEO STUDIO does not classify, detect, or enforce
 * which uploaded file matches which view; this is guidance only, shown
 * to help the customer choose good photos.
 */
export const RECOMMENDED_REFERENCE_VIEWS: ReadonlyArray<{ readonly label: string; readonly description: string }> = [
  { label: "Front", description: "Looking directly at the camera, full face clearly visible." },
  { label: "Left 3/4", description: "Head turned slightly to one side — both eyes still visible if possible." },
  { label: "Right 3/4", description: "The same kind of angle from the opposite side." },
  { label: "Side / profile", description: "A stronger side angle that shows the shape of the face, nose, jaw and head." },
  { label: "Face + hair", description: "A clear photo showing the face together with the person's normal hair, hairline and length." },
];
